import React, { useState, useEffect, useMemo, useCallback, useRef, Fragment } from "react";
import { Link } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { fetchProducts } from "../../../api/product";
import axios from "../../../api/axios";
import { warehouseStockApi } from "../../../api/inventory/warehouse-stock";
import { debounce } from "lodash";
import {
  MagnifyingGlassIcon,
  XMarkIcon,
  CheckIcon,
  FunnelIcon,
  XCircleIcon,
  ChevronRightIcon,
  ChevronDownIcon,
  HomeIcon,
  InformationCircleIcon,
  ListBulletIcon,
  ArrowsRightLeftIcon,
  ArrowTopRightOnSquareIcon,
} from "@heroicons/react/24/outline";
import { Dialog, Transition } from "@headlessui/react";
import { DocumentImage } from "../../dashboard/documents/utils";
import { productTypeOptions, findWarehouseStatusById } from "../../dashboard/products/product-data";
import { PMDialog } from "../PMDialog";

/** Numeric id of successor product, if this product is marked as replaced. */
function getReplacementProductId(product) {
  if (!product) return null;
  const fromNested = product.replaced_by_product?.id;
  if (fromNested != null && fromNested !== "") {
    const n = parseInt(fromNested, 10);
    return Number.isNaN(n) ? null : n;
  }
  const rid = product.replaced_by_product_id;
  if (rid != null && rid !== "") {
    const n = parseInt(rid, 10);
    return Number.isNaN(n) ? null : n;
  }
  return null;
}

const PRODUCT_SELECTOR_LIST_FIELDS =
  "id,title,displayTitle,item_number,unit,unit_id,sales_price,purchase_price,status,product_type,main_picture,active,warehouse_status_type_id,replaced_by_product_id,replaced_by_product";

/**
 * Modern Product Selector Component V2
 * 
 * Features:
 * - Hierarchical category navigation with sidebar
 * - Fast product fetching with debounced search
 * - Filter inactive and expired products
 * - Category filtering with parent/child navigation
 * - Multiple selection support
 * - Responsive design for mobile and desktop
 * - Compact and user-friendly
 * 
 * @param {Object} props
 * @param {boolean} props.open - Whether the selector is open
 * @param {Array} props.selectedProducts - Array of selected product objects
 * @param {Function} props.onChange - Callback when selection changes (receives array of products)
 * @param {Function} props.onClose - Callback when selector should close
 * @param {boolean} props.multiple - Allow multiple selection (default: true)
 * @param {Array} props.filters - Additional filters to apply
 * @param {boolean} props.showHeader - Show header section (default: true)
 * @param {boolean} props.showWarehouseOnlyDefault - Show only warehouse products by default (default: false)
 * @param {boolean} props.showQuantityInput - Show quantity input for selected products (default: true)
 * @param {Function} [props.onConfirmSelection] - Optional callback for explicit multi-select confirmation
 * @param {string} [props.confirmSelectionLabel] - Optional label for explicit multi-select confirmation button
 * @param {boolean} props.embeddedInParentDialog - When true, render only the panel (parent supplies modal shell, e.g. ProductSelectorDialog or MUI Dialog)
 * @param {string} [props.pmdialogTitle] - i18n key for PMDialog title when not embedded (default: select product(s))
 * @param {string} [props.pmdialogSize] - PMDialog max width token when not embedded (default: 7xl)
 * @param {boolean} [props.pmdialogDense] - PMDialog dense layout (full-height mobile, minimal padding) when not embedded
 * @param {boolean} [props.hideCategoryAndSystemNav] - Hide category tree and system-product sidebar (flat list + search only)
 * @param {boolean} [props.defaultShowActiveOnly] - Initial "active only" toggle when dialog opens (default true)
 * @param {boolean} [props.defaultShowExpired] - Initial "show expired" toggle when dialog opens (default false)
 * @param {string} [props.searchPlaceholder] - Override main search input placeholder
 * @param {boolean} [props.searchOnly] - Hide all filter UI (funnel/chips); list uses only search + props.filters
 * @param {boolean} [props.defaultFilterSystemsOnly] - Initial "System" list filter (hasSpareParts) when dialog opens
 */
export default function ModernProductSelectorV2({
  open = false,
  selectedProducts = [],
  onChange,
  onClose,
  multiple = true,
  filters = [],
  showHeader = true,
  showWarehouseOnlyDefault = false,
  showQuantityInput = true,
  onConfirmSelection,
  confirmSelectionLabel = "Legg til",
  embeddedInParentDialog = false,
  pmdialogTitle,
  pmdialogSize = "7xl",
  pmdialogDense = false,
  hideCategoryAndSystemNav = false,
  defaultShowActiveOnly = true,
  defaultShowExpired = false,
  searchPlaceholder,
  searchOnly = false,
  defaultFilterSystemsOnly = false,
}) {
  const { t } = useTranslation();
  const [searchQuery, setSearchQuery] = useState("");
  const [products, setProducts] = useState([]);
  const [isLoading, setIsLoading] = useState(false);
  const [error, setError] = useState(null);
  const [currentPage, setCurrentPage] = useState(1);
  const [_totalCount, setTotalCount] = useState(0);
  const [hasMore, setHasMore] = useState(false);
  const [showFilters, setShowFilters] = useState(showWarehouseOnlyDefault); // Open filters by default if showWarehouseOnlyDefault is true
  const [showActiveOnly, setShowActiveOnly] = useState(() => defaultShowActiveOnly);
  const [showExpired, setShowExpired] = useState(() => defaultShowExpired);
  const [showWarehouseOnly, setShowWarehouseOnly] = useState(showWarehouseOnlyDefault); // Show only warehouse products
  const [selectedCategory, setSelectedCategory] = useState(null);
  const [selectedCategoryIds, setSelectedCategoryIds] = useState([]); // For filtering by category_ids
  const [categories, setCategories] = useState([]);
  const [expandedCategories, setExpandedCategories] = useState(new Set());
  const [categoryBreadcrumb, setCategoryBreadcrumb] = useState([]); // Track navigation path
  const [categoriesSearchQuery, setCategoriesSearchQuery] = useState(""); // Search query for categories
  const [selectedIds, setSelectedIds] = useState(() => {
    // Ensure IDs are converted to numbers for consistent comparison
    const ids = selectedProducts.map((p) => {
      const id = typeof p === 'object' ? p.id : p;
      return parseInt(id);
    }).filter(id => !isNaN(id));
    return new Set(ids);
  });
  const [productQuantities, setProductQuantities] = useState(() => {
    const quantities = new Map();
    selectedProducts.forEach((p) => {
      const id = typeof p === 'object' ? p.id : p;
      const numId = parseInt(id);
      if (!isNaN(numId)) {
        quantities.set(numId, p.quantity || 1);
      }
    });
    return quantities;
  });
  const [isLoadingCategories, setIsLoadingCategories] = useState(false);
  const [showCategorySelectorMobile, setShowCategorySelectorMobile] = useState(false);
  
  // System products (products with spare parts)
  const [systemProducts, setSystemProducts] = useState([]);
  const [isLoadingSystemProducts, setIsLoadingSystemProducts] = useState(false);
  const [systemProductsSearchQuery, setSystemProductsSearchQuery] = useState("");
  const [selectedSystemProductId, setSelectedSystemProductId] = useState(null);
  const [showSystemProductsMobile, setShowSystemProductsMobile] = useState(false);
  const [activeView, setActiveView] = useState("categories"); // "categories" or "systems"
  const [showProductTypeDialog, setShowProductTypeDialog] = useState(false);
  const [showProductDetailsDialog, setShowProductDetailsDialog] = useState(false);
  const [selectedProductForDetails, setSelectedProductForDetails] = useState(null);
  const [productStockData, setProductStockData] = useState(null);
  const [isLoadingProductDetails, setIsLoadingProductDetails] = useState(false);
  const [productDetails, setProductDetails] = useState(null);
  const [showSelectedOnly, setShowSelectedOnly] = useState(false); // Show only selected products
  const [filterSystemsOnly, setFilterSystemsOnly] = useState(() => defaultFilterSystemsOnly);

  const perPage = 20;
  /** Monotonic id so only the latest in-flight list fetch may update UI (avoids stale debounce / parallel responses). */
  const productListFetchSeqRef = useRef(0);

  // Update selectedIds and quantities when selectedProducts prop changes
  useEffect(() => {
    if (!open) return;
    const ids = selectedProducts
      .map((p) => {
        const id = typeof p === "object" ? p.id : p;
        return parseInt(id, 10);
      })
      .filter((id) => !isNaN(id));

    setSelectedIds((prev) => {
      if (prev.size !== ids.length) return new Set(ids);
      for (const id of ids) {
        if (!prev.has(id)) return new Set(ids);
      }
      return prev;
    });

    const nextQty = new Map(
      selectedProducts
        .map((p) => {
          const id = typeof p === "object" ? p.id : p;
          return [parseInt(id, 10), p.quantity || 1];
        })
        .filter(([id]) => !isNaN(id))
    );
    setProductQuantities((prev) => {
      if (prev.size !== nextQty.size) return nextQty;
      for (const [id, q] of nextQty) {
        if (prev.get(id) !== q) return nextQty;
      }
      return prev;
    });
  }, [selectedProducts, open]);

  // Reset state when dialog opens/closes
  useEffect(() => {
    if (open) {
      setSearchQuery("");
      setCurrentPage(1);
      setError(null);
      setShowFilters(
        searchOnly
          ? false
          : showWarehouseOnlyDefault || hideCategoryAndSystemNav
      );
      setSelectedCategory(null);
      setSelectedCategoryIds([]);
      setCategoryBreadcrumb([]);
      setSelectedSystemProductId(null);
      setShowActiveOnly(defaultShowActiveOnly);
      setShowWarehouseOnly(showWarehouseOnlyDefault); // Reset to prop default
      setShowExpired(defaultShowExpired);
      setShowSelectedOnly(false);
      setFilterSystemsOnly(defaultFilterSystemsOnly);
      if (!hideCategoryAndSystemNav) {
        loadCategories();
      } else {
        setCategories([]);
        setExpandedCategories(new Set());
      }
    } else {
      // Reset filters when closed
      setShowActiveOnly(defaultShowActiveOnly);
      setShowExpired(defaultShowExpired);
      setShowWarehouseOnly(showWarehouseOnlyDefault);
      setSelectedCategory(null);
      setSelectedCategoryIds([]);
      setCategoryBreadcrumb([]);
      setExpandedCategories(new Set());
      setShowCategorySelectorMobile(false);
      setSystemProductsSearchQuery("");
      setSelectedSystemProductId(null);
      setShowSystemProductsMobile(false);
      setActiveView("categories");
      setShowProductTypeDialog(false);
      setShowSelectedOnly(false);
      setFilterSystemsOnly(defaultFilterSystemsOnly);
    }
  }, [
    open,
    hideCategoryAndSystemNav,
    showWarehouseOnlyDefault,
    defaultShowActiveOnly,
    defaultShowExpired,
    searchOnly,
    defaultFilterSystemsOnly,
  ]);

  // Load categories from API
  const loadCategories = useCallback(async () => {
    setIsLoadingCategories(true);
    try {
      const response = await axios.get(`/products/categories/list?parents=true`, {
        params: {
          page: 1,
          per_page: 1000, // Get all categories
        },
      });
      
      if (response?.data?.data) {
        setCategories(response.data.data);
        // Expand first level by default
        const firstLevelIds = response.data.data.map(cat => cat.id);
        setExpandedCategories(new Set(firstLevelIds));
      }
    } catch (err) {
      console.error("Error loading categories:", err);
    } finally {
      setIsLoadingCategories(false);
    }
  }, []);

  // Debounced search for system products
  const debouncedSystemProductsSearch = useMemo(
    () =>
      debounce(async (query) => {
        setIsLoadingSystemProducts(true);
        try {
          const params = {
            query: query || undefined,
            page: 1,
            per_page: 20, // Limit to 20 system products for better performance
            sortBy: "id",
            sort: "desc",
            filters: [{ name: "hasSpareParts", value: true }], // Filter for products with spare parts
            fields: "id,title,displayTitle,item_number,replaced_by_product_id,replaced_by_product",
          };

          const response = await fetchProducts(params);

          if (response && response.data) {
            setSystemProducts(response.data);
          } else {
            setSystemProducts([]);
          }
        } catch (err) {
          console.error("Error fetching system products:", err);
          setSystemProducts([]);
        } finally {
          setIsLoadingSystemProducts(false);
        }
      }, 300),
    []
  );

  // Load system products when view changes, search query changes, or mobile dialog opens
  useEffect(() => {
    if (!open || hideCategoryAndSystemNav) return;

    if (activeView === "systems" || showSystemProductsMobile) {
      debouncedSystemProductsSearch(systemProductsSearchQuery);
    }

    return () => {
      debouncedSystemProductsSearch.cancel();
    };
  }, [
    open,
    hideCategoryAndSystemNav,
    activeView,
    systemProductsSearchQuery,
    showSystemProductsMobile,
    debouncedSystemProductsSearch,
  ]);

  // Recursive function to get all category IDs including children
  const getAllCategoryIds = useCallback((category) => {
    const ids = [category.id];
    if (category.children && category.children.length > 0) {
      category.children.forEach(child => {
        ids.push(...getAllCategoryIds(child));
      });
    }
    return ids;
  }, []);

  // Find path to a category in the tree
  const findCategoryPath = useCallback((catList, targetId, path = []) => {
    for (const cat of catList) {
      const currentPath = [...path, cat];
      if (cat.id === targetId) {
        return currentPath;
      }
      if (cat.children && cat.children.length > 0) {
        const found = findCategoryPath(cat.children, targetId, currentPath);
        if (found) return found;
      }
    }
    return null;
  }, []);

  // Handle category navigation (clicking on category to navigate)
  const handleCategoryNavigate = useCallback((category, event) => {
    event.stopPropagation();
    
    // If clicking on already selected category, deselect
    if (selectedCategoryIds.includes(category.id) && categoryBreadcrumb.length > 0 && categoryBreadcrumb[categoryBreadcrumb.length - 1].id === category.id) {
      setSelectedCategoryIds([]);
      setCategoryBreadcrumb([]);
      setCurrentPage(1);
      // Close mobile category selector when deselecting
      setShowCategorySelectorMobile(false);
      return;
    }
    
    // Build breadcrumb path - find parent path from root
    const path = findCategoryPath(categories, category.id);
    const newBreadcrumb = path || [category];
    
    setCategoryBreadcrumb(newBreadcrumb);
    
    // Select this category and all its children
    const allIds = getAllCategoryIds(category);
    setSelectedCategoryIds(allIds);
    setCurrentPage(1);
    
    // Expand this category
    setExpandedCategories(prev => new Set([...prev, category.id]));
    
    // Close mobile category selector after selection
    setShowCategorySelectorMobile(false);
  }, [selectedCategoryIds, categoryBreadcrumb, categories, getAllCategoryIds, findCategoryPath]);

  // Handle breadcrumb navigation
  const handleBreadcrumbClick = useCallback((index) => {
    if (index === -1) {
      // Clicked "Alle kategorier"
      setCategoryBreadcrumb([]);
      setSelectedCategoryIds([]);
      setCurrentPage(1);
      setShowCategorySelectorMobile(false);
      return;
    }
    
    const newBreadcrumb = categoryBreadcrumb.slice(0, index + 1);
    setCategoryBreadcrumb(newBreadcrumb);
    
    if (newBreadcrumb.length > 0) {
      const category = newBreadcrumb[newBreadcrumb.length - 1];
      const allIds = getAllCategoryIds(category);
      setSelectedCategoryIds(allIds);
    } else {
      setSelectedCategoryIds([]);
    }
    setCurrentPage(1);
  }, [categoryBreadcrumb, getAllCategoryIds]);

  // Toggle category expansion
  const toggleCategoryExpansion = useCallback((categoryId, event) => {
    event.stopPropagation();
    setExpandedCategories(prev => {
      const newSet = new Set(prev);
      if (newSet.has(categoryId)) {
        newSet.delete(categoryId);
      } else {
        newSet.add(categoryId);
      }
      return newSet;
    });
  }, []);

  // Debounced search function
  const debouncedSearch = useMemo(
    () =>
      debounce(async (query, page, filters, categoryIds, systemProductId, showActiveOnlyParam, showExpiredParam, showWarehouseOnlyParam, selectedCategoryParam, selectedIdsArray, applySystemsOnlyFilter) => {
        const seq = ++productListFetchSeqRef.current;
        setIsLoading(true);
        setError(null);

        try {
          // Build filters array
          const allFilters = Array.isArray(filters) ? [...filters] : [];
          if (applySystemsOnlyFilter) {
            const hasSpare = allFilters.some((f) => f && f.name === "hasSpareParts");
            if (!hasSpare) {
              allFilters.push({ name: "hasSpareParts", value: true });
            }
          }
          if (systemProductId) {
            allFilters.push({ name: "spareparts", value: systemProductId });
          }
          // Non-expired warehouse types (1–3), same as webshop; API also includes warehouse_enabled
          // with unset status (nil/0).
          if (showWarehouseOnlyParam) {
            allFilters.push({ name: "warehouse_status_type", value: JSON.stringify([1, 2, 3]) });
          }

          const params = {
            query: query || undefined,
            page: page,
            per_page: perPage,
            sortBy: "id",
            sort: "desc",
            // API: omitting show_inactive still restricts to active:true; pass true to include inactive
            show_inactive: showActiveOnlyParam ? false : true,
            show_expired: showExpiredParam,
            product_type: selectedCategoryParam || undefined,
            category_ids: categoryIds.length > 0 ? categoryIds : undefined,
            ids: selectedIdsArray && selectedIdsArray.length > 0 ? selectedIdsArray.join(",") : undefined,
            filters: allFilters.length > 0 ? allFilters : undefined,
            fields: PRODUCT_SELECTOR_LIST_FIELDS,
          };

          const response = await fetchProducts(params);
          if (seq !== productListFetchSeqRef.current) return;

          if (response && response.data) {
            if (page === 1) {
              setProducts(response.data);
            } else {
              setProducts((prev) => [...prev, ...response.data]);
            }

            setTotalCount(response.total_count || response.data.length);
            setHasMore(
              response.data.length === perPage &&
                (page * perPage < (response.total_count || 0))
            );
          } else {
            setProducts([]);
            setTotalCount(0);
            setHasMore(false);
          }
        } catch (err) {
          console.error("Error fetching products:", err);
          if (seq !== productListFetchSeqRef.current) return;
          setError("Kunne ikke laste produkter");
          setProducts([]);
        } finally {
          if (seq === productListFetchSeqRef.current) {
            setIsLoading(false);
          }
        }
      }, 300),
    // All query inputs are passed as arguments to the debounced fn; stable identity avoids
    // effect loops when parent passes new `filters` array references each render.
    []
  );

  // Immediate search function (for filter changes)
  const immediateSearch = useCallback(async (query, page, filters, categoryIds, systemProductId, showActiveOnlyParam, showExpiredParam, showWarehouseOnlyParam, selectedCategoryParam, selectedIdsArray, applySystemsOnlyFilter) => {
    const seq = ++productListFetchSeqRef.current;
    setIsLoading(true);
    setError(null);

    try {
      // Build filters array
      const allFilters = Array.isArray(filters) ? [...filters] : [];
      if (applySystemsOnlyFilter) {
        const hasSpare = allFilters.some((f) => f && f.name === "hasSpareParts");
        if (!hasSpare) {
          allFilters.push({ name: "hasSpareParts", value: true });
        }
      }
      if (systemProductId) {
        allFilters.push({ name: "spareparts", value: systemProductId });
      }
      // Non-expired warehouse types (1–3), same as webshop; API also includes warehouse_enabled
      // with unset status (nil/0).
      if (showWarehouseOnlyParam) {
        allFilters.push({ name: "warehouse_status_type", value: JSON.stringify([1, 2, 3]) });
      }

      const params = {
        query: query || undefined,
        page: page,
        per_page: perPage,
        sortBy: "id",
        sort: "desc",
        show_inactive: showActiveOnlyParam ? false : true,
        show_expired: showExpiredParam,
        product_type: selectedCategoryParam || undefined,
        category_ids: categoryIds.length > 0 ? categoryIds : undefined,
        ids: selectedIdsArray && selectedIdsArray.length > 0 ? selectedIdsArray.join(",") : undefined,
        filters: allFilters.length > 0 ? allFilters : undefined,
        fields: PRODUCT_SELECTOR_LIST_FIELDS,
      };

      const response = await fetchProducts(params);
      if (seq !== productListFetchSeqRef.current) return;

      if (response && response.data) {
        if (page === 1) {
          setProducts(response.data);
        } else {
          setProducts((prev) => [...prev, ...response.data]);
        }

        setTotalCount(response.total_count || response.data.length);
        setHasMore(
          response.data.length === perPage &&
            (page * perPage < (response.total_count || 0))
        );
      } else {
        setProducts([]);
        setTotalCount(0);
        setHasMore(false);
      }
    } catch (err) {
      console.error("Error fetching products:", err);
      if (seq !== productListFetchSeqRef.current) return;
      setError("Kunne ikke laste produkter");
      setProducts([]);
    } finally {
      if (seq === productListFetchSeqRef.current) {
        setIsLoading(false);
      }
    }
  }, []);

  // Stable keys so inline `filters={[]}` / new Set references do not retrigger fetch in a loop
  const filtersSignature = JSON.stringify(Array.isArray(filters) ? filters : []);
  const selectedIdsSignature = [...selectedIds].sort((a, b) => a - b).join(",");

  // Fetch products when search query or filters change
  useEffect(() => {
    if (!open) return;

    setCurrentPage(1);
    
    // Cancel any pending debounced calls
    debouncedSearch.cancel();
    
    const selectedIdsArray = showSelectedOnly
      ? selectedIdsSignature.length > 0
        ? selectedIdsSignature.split(",").map((s) => parseInt(s, 10)).filter((n) => !isNaN(n))
        : []
      : null;

    const applySystemsOnlyFilter = !searchOnly && filterSystemsOnly;

    // For search query changes, use debounce. For filter changes (like selectedCategory), call immediately
    if (searchQuery) {
      debouncedSearch(searchQuery, 1, filters, selectedCategoryIds, selectedSystemProductId, showActiveOnly, showExpired, showWarehouseOnly, selectedCategory, selectedIdsArray, applySystemsOnlyFilter);
    } else {
      immediateSearch(searchQuery, 1, filters, selectedCategoryIds, selectedSystemProductId, showActiveOnly, showExpired, showWarehouseOnly, selectedCategory, selectedIdsArray, applySystemsOnlyFilter);
    }

    return () => {
      debouncedSearch.cancel();
    };
  }, [
    searchQuery,
    showActiveOnly,
    showExpired,
    showWarehouseOnly,
    selectedCategory,
    selectedSystemProductId,
    open,
    filtersSignature,
    selectedCategoryIds,
    showSelectedOnly,
    selectedIdsSignature,
    debouncedSearch,
    immediateSearch,
    searchOnly,
    filterSystemsOnly,
  ]);

  // Load more products
  const loadMore = useCallback(() => {
    if (!isLoading && hasMore) {
      const nextPage = currentPage + 1;
      setCurrentPage(nextPage);
      // Get selected IDs array if showSelectedOnly is true
      const selectedIdsArray = showSelectedOnly ? Array.from(selectedIds) : null;
      const applySystemsOnlyFilter = !searchOnly && filterSystemsOnly;
      // Use immediate search for load more (no debounce needed)
      immediateSearch(searchQuery, nextPage, filters, selectedCategoryIds, selectedSystemProductId, showActiveOnly, showExpired, showWarehouseOnly, selectedCategory, selectedIdsArray, applySystemsOnlyFilter);
    }
  }, [isLoading, hasMore, currentPage, searchQuery, filters, selectedCategoryIds, selectedSystemProductId, showActiveOnly, showExpired, showWarehouseOnly, selectedCategory, showSelectedOnly, selectedIds, immediateSearch, searchOnly, filterSystemsOnly]);

  // Handle system product selection
  const handleSystemProductSelect = useCallback((systemProduct) => {
    if (selectedSystemProductId === systemProduct.id) {
      // Deselect
      setSelectedSystemProductId(null);
    } else {
      // Select
      setSelectedSystemProductId(systemProduct.id);
    }
    setCurrentPage(1);
    setShowSystemProductsMobile(false);
  }, [selectedSystemProductId]);

  // Handle showing product details
  const handleShowProductDetails = useCallback(async (product) => {
    setSelectedProductForDetails(product);
    setShowProductDetailsDialog(true);
    setIsLoadingProductDetails(true);
    setProductStockData(null);
    setProductDetails(null);

    try {
      // Fetch full product details
      const productResponse = await fetchProducts({
        ids: product.id.toString(),
        page: 1,
        per_page: 1,
        fields:
          "id,title,displayTitle,item_number,description,short_description,main_picture,sales_price,purchase_price,warehouse,active,warehouse_status_type_id,product_type,unit_id,replaced_by_product_id,replaced_by_product",
      });

      let fetchedProductDetails = null;
      if (productResponse?.data && productResponse.data.length > 0) {
        fetchedProductDetails = productResponse.data[0];
        setProductDetails(fetchedProductDetails);
      }

      // Fetch stock data - try to fetch for all products, API will handle if product doesn't have warehouse
      try {
        const stockResponse = await warehouseStockApi.getProductStock(product.id);
        if (stockResponse?.success && stockResponse.data) {
          setProductStockData(stockResponse.data);
        } else if (stockResponse?.data) {
          // Handle case where API returns data directly
          setProductStockData(stockResponse.data);
        }
      } catch (error) {
        console.error("Error fetching stock data:", error);
        // Don't show error, just don't display stock info
      }
    } catch (error) {
      console.error("Error fetching product details:", error);
    } finally {
      setIsLoadingProductDetails(false);
    }
  }, []);

  // Update quantity for a product
  const updateQuantity = useCallback(
    (productId, quantity) => {
      const newQuantities = new Map(productQuantities);
      const qty = parseFloat(quantity) || 1;
      const id = parseInt(productId);
      if (isNaN(id)) return;
      
      newQuantities.set(id, qty);
      setProductQuantities(newQuantities);

      // Update selected products with new quantity
      const selectedFromCurrent = products
        .filter((p) => {
          const pid = parseInt(p.id);
          return !isNaN(pid) && selectedIds.has(pid);
        })
        .map((p) => {
          const pid = parseInt(p.id);
          return {
            ...p,
            quantity: newQuantities.get(pid) || 1,
          };
        });
      const selectedFromPrevious = selectedProducts
        .filter((p) => {
          const pid = typeof p === 'object' ? parseInt(p.id) : parseInt(p);
          if (isNaN(pid)) return false;
          const existsInCurrent = products.some(prod => parseInt(prod.id) === pid);
          return selectedIds.has(pid) && !existsInCurrent;
        })
        .map((p) => {
          const pid = typeof p === 'object' ? parseInt(p.id) : parseInt(p);
          return {
            ...p,
            id: pid,
            quantity: newQuantities.get(pid) || p.quantity || 1,
          };
        });
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }
    },
    [productQuantities, products, selectedIds, selectedProducts, onChange]
  );

  // Toggle product selection
  const toggleProduct = useCallback(
    (product) => {
      const newSelectedIds = new Set(selectedIds);
      const newQuantities = new Map(productQuantities);
      
      // Ensure consistent type comparison (convert to number)
      const productId = parseInt(product.id);
      if (isNaN(productId)) return;

      if (newSelectedIds.has(productId)) {
        newSelectedIds.delete(productId);
        newQuantities.delete(productId);
      } else {
        if (!multiple) {
          newSelectedIds.clear();
          newQuantities.clear();
        }
        newSelectedIds.add(productId);
        // Set default quantity to 1 if not already set
        if (!newQuantities.has(productId)) {
          newQuantities.set(productId, 1);
        }
      }

      setSelectedIds(newSelectedIds);
      setProductQuantities(newQuantities);

      // Get selected products - prioritize products from current list (they have full data)
      const selectedFromCurrent = products
        .filter((p) => {
          const id = parseInt(p.id);
          return !isNaN(id) && newSelectedIds.has(id);
        })
        .map((p) => {
          const id = parseInt(p.id);
          return {
            ...p,
            quantity: newQuantities.get(id) || 1,
          };
        });
      const selectedFromPrevious = selectedProducts
        .filter((p) => {
          const id = typeof p === 'object' ? parseInt(p.id) : parseInt(p);
          if (isNaN(id)) return false;
          const existsInCurrent = products.some(prod => parseInt(prod.id) === id);
          return newSelectedIds.has(id) && !existsInCurrent;
        })
        .map((p) => {
          const id = typeof p === 'object' ? parseInt(p.id) : parseInt(p);
          return {
            ...p,
            id: id, // Ensure ID is a number
            quantity: newQuantities.get(id) || p.quantity || 1,
          };
        });
      const selected = [...selectedFromCurrent, ...selectedFromPrevious];

      if (onChange) {
        onChange(selected);
      }

      // Auto-close for single selection
      if (!multiple && newSelectedIds.size > 0 && onClose) {
        setTimeout(() => {
          onClose();
        }, 300);
      }
    },
    [
      selectedIds,
      productQuantities,
      products,
      selectedProducts,
      multiple,
      onChange,
      onClose,
    ]
  );

  // Clear selection
  const clearSelection = useCallback(() => {
    setSelectedIds(new Set());
    setProductQuantities(new Map());
    if (onChange) {
      onChange([]);
    }
  }, [onChange]);

  // Check if product is selected
  const isSelected = useCallback(
    (productId) => {
      // Ensure consistent type comparison (convert to number)
      const id = parseInt(productId);
      return !isNaN(id) && selectedIds.has(id);
    },
    [selectedIds]
  );

  // Filter categories based on search query
  const filterCategories = useCallback((categoryList, searchQuery, expandedSet = new Set()) => {
    if (!searchQuery || searchQuery.trim() === "") {
      return { filtered: categoryList, expandedIds: expandedSet };
    }

    const query = searchQuery.toLowerCase().trim();
    const filtered = [];
    const expandedIds = new Set(expandedSet);

    const matchesSearch = (category) => {
      return category.title?.toLowerCase().includes(query);
    };

    const hasMatchingChildren = (category, searchQuery) => {
      if (!category.children || category.children.length === 0) {
        return false;
      }
      return category.children.some(child => {
        if (matchesSearch(child)) return true;
        return hasMatchingChildren(child, searchQuery);
      });
    };

    categoryList.forEach((category) => {
      const categoryMatches = matchesSearch(category);
      const childrenMatch = hasMatchingChildren(category, searchQuery);
      
      if (categoryMatches || childrenMatch) {
        // If category matches or has matching children, include it
        const filteredCategory = { ...category };
        if (category.children && category.children.length > 0) {
          // Recursively filter children
          const childResult = filterCategories(category.children, searchQuery, expandedIds);
          filteredCategory.children = childResult.filtered;
          // Merge expanded IDs from children
          childResult.expandedIds.forEach(id => expandedIds.add(id));
          
          // If category itself matches or has matching children, expand it to show children
          if (categoryMatches || childrenMatch) {
            expandedIds.add(category.id);
          }
        }
        filtered.push(filteredCategory);
      }
    });

    return { filtered, expandedIds };
  }, []);

  // Get filtered categories
  const filteredCategoriesResult = useMemo(() => {
    return filterCategories(categories, categoriesSearchQuery, new Set());
  }, [categories, categoriesSearchQuery, filterCategories]);

  const filteredCategories = filteredCategoriesResult.filtered;

  // Update expanded categories when search query changes
  useEffect(() => {
    if (categoriesSearchQuery && filteredCategoriesResult.expandedIds.size > 0) {
      setExpandedCategories(filteredCategoriesResult.expandedIds);
    } else if (!categoriesSearchQuery) {
      // Reset to default expansion when search is cleared
      if (categories.length > 0) {
        const firstLevelIds = categories.map(cat => cat.id);
        setExpandedCategories(new Set(firstLevelIds));
      }
    }
  }, [categoriesSearchQuery, filteredCategoriesResult.expandedIds, categories]);

  // Render category tree recursively
  const renderCategoryTree = useCallback((categoryList, level = 0) => {
    return categoryList.map((category) => {
      const isExpanded = expandedCategories.has(category.id);
      const isSelected = selectedCategoryIds.includes(category.id) && 
        categoryBreadcrumb.length > 0 && 
        categoryBreadcrumb[categoryBreadcrumb.length - 1].id === category.id;
      const hasChildren = category.children && category.children.length > 0;

      return (
        <div key={category.id} className="select-none">
          <div
            className={`flex items-center gap-2 px-2 py-1.5 rounded-md cursor-pointer transition-colors ${
              isSelected
                ? "bg-blue-100 text-blue-700 font-medium"
                : "hover:bg-gray-100 text-gray-700"
            }`}
            style={{ paddingLeft: `${level * 16 + 8}px` }}
            onClick={(e) => handleCategoryNavigate(category, e)}
          >
            {hasChildren && (
              <button
                onClick={(e) => toggleCategoryExpansion(category.id, e)}
                className="p-0.5 hover:bg-gray-200 rounded flex-shrink-0"
              >
                {isExpanded ? (
                  <ChevronDownIcon className="h-4 w-4" />
                ) : (
                  <ChevronRightIcon className="h-4 w-4" />
                )}
              </button>
            )}
            {!hasChildren && <div className="w-5 flex-shrink-0" />}
            <span className="flex-1 text-sm truncate" title={category.title}>{category.title}</span>
            {isSelected && (
              <CheckIcon className="h-4 w-4 text-blue-600 flex-shrink-0" />
            )}
          </div>
          {isExpanded && hasChildren && (
            <div className="mt-1">
              {renderCategoryTree(category.children, level + 1)}
            </div>
          )}
        </div>
      );
    });
  }, [expandedCategories, selectedCategoryIds, categoryBreadcrumb, handleCategoryNavigate, toggleCategoryExpansion]);

  if (!open) return null;

  const resolvedSearchPlaceholder =
    searchPlaceholder || t("product.selector.search_placeholder");
  const emptyListHintFlat = t("product.selector.empty_flat_list");

  const pmdialogTitleKey =
    pmdialogTitle ||
    (multiple
      ? "inventory.count.select_products"
      : "inventory.count.select_product");
  const showConfirmSelection = Boolean(multiple && onConfirmSelection);

  const mainPanel = (
    <div className="w-full flex flex-col bg-white rounded-none sm:rounded-lg border-0 sm:border border-gray-200 shadow-none sm:shadow-sm overflow-hidden" style={{ height: '100%', maxHeight: '100%' }}>
      {/* Header */}
      {showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          <div className="flex items-center justify-between mb-2 sm:mb-3">
            {embeddedInParentDialog && (
            <h3 className="text-base sm:text-lg font-semibold text-gray-900">
              {multiple ? "Velg produkter" : "Velg produkt"}
            </h3>
            )}
            {!embeddedInParentDialog && <div className="flex-1" aria-hidden="true" />}
            <div className="flex items-center space-x-2">
              {selectedIds.size > 0 && (
                <>
                  <button
                    onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                    className={`p-1.5 rounded-md transition-colors ${
                      showSelectedOnly
                        ? "bg-blue-100 text-blue-600"
                        : "text-gray-400 hover:text-gray-600"
                    }`}
                    title="Vis kun valgte produkter"
                  >
                    <ListBulletIcon className="h-5 w-5" />
                  </button>
                  <button
                    onClick={clearSelection}
                    className="text-sm text-gray-500 hover:text-gray-700"
                    title="Fjern alle valg"
                  >
                    <XCircleIcon className="h-5 w-5" />
                  </button>
                </>
              )}
              {!searchOnly && (
              <button
                onClick={() => setShowFilters(!showFilters)}
                className={`p-1.5 rounded-md transition-colors ${
                  showFilters
                    ? "bg-blue-100 text-blue-600"
                    : "text-gray-400 hover:text-gray-600"
                }`}
                title="Vis filtre"
              >
                <FunnelIcon className="h-5 w-5" />
              </button>
              )}
              {/* Show filter indicator when filters are active */}
              {!searchOnly &&
                (showActiveOnly ||
                  showExpired ||
                  showWarehouseOnly ||
                  selectedCategory ||
                  showSelectedOnly ||
                  filterSystemsOnly) && (
                <span className="inline-flex items-center px-2 py-0.5 rounded-full text-xs font-medium bg-blue-100 text-blue-800">
                  {showSelectedOnly ? "Viser valgte" : "Filter aktivt"}
                </span>
              )}
              {onClose && embeddedInParentDialog && (
                <button
                  onClick={onClose}
                  className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                  title="Lukk"
                >
                  <XMarkIcon className="h-5 w-5" />
                </button>
              )}
            </div>
          </div>

          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Mobile: Category and System Selector Buttons */}
          {!hideCategoryAndSystemNav && (
          <div className="md:hidden mt-2 space-y-2">
            <button
              onClick={() => setShowCategorySelectorMobile(true)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">
                {categoryBreadcrumb.length > 0
                  ? categoryBreadcrumb[categoryBreadcrumb.length - 1].title
                  : "Velg kategori"}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </button>
            <button
              onClick={() => setShowSystemProductsMobile(true)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">
                {selectedSystemProductId
                  ? systemProducts.find(p => p.id === selectedSystemProductId)?.displayTitle || 
                    systemProducts.find(p => p.id === selectedSystemProductId)?.title ||
                    "Systemprodukt valgt"
                  : "Velg systemprodukt"}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          )}

          {/* Breadcrumb Navigation - Desktop */}
          {!hideCategoryAndSystemNav && (categoryBreadcrumb.length > 0 || selectedSystemProductId) && (
            <div className="hidden md:flex mt-2 items-center gap-1 text-sm text-gray-600 flex-wrap">
              {categoryBreadcrumb.length > 0 && (
                <>
                  <button
                    onClick={() => handleBreadcrumbClick(-1)}
                    className="flex items-center gap-1 hover:text-blue-600"
                  >
                    <HomeIcon className="h-4 w-4" />
                    <span>Alle kategorier</span>
                  </button>
                  {categoryBreadcrumb.map((category, index) => (
                    <React.Fragment key={category.id}>
                      <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                      <button
                        onClick={() => handleBreadcrumbClick(index)}
                        className="hover:text-blue-600 truncate max-w-[120px]"
                        title={category.title}
                      >
                        {category.title}
                      </button>
                    </React.Fragment>
                  ))}
                </>
              )}
              {selectedSystemProductId && (
                <>
                  {categoryBreadcrumb.length > 0 && (
                    <span className="mx-2 text-gray-400">|</span>
                  )}
                  <span className="text-gray-700">
                    System: {systemProducts.find(p => p.id === selectedSystemProductId)?.displayTitle || 
                             systemProducts.find(p => p.id === selectedSystemProductId)?.title || 
                             "Ukjent"}
                  </span>
                  <button
                    onClick={() => setSelectedSystemProductId(null)}
                    className="ml-2 text-gray-400 hover:text-red-600"
                    title="Fjern systemprodukt-filter"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Filters - Minimal compact design */}
          {showFilters && !searchOnly && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowActiveOnly(!showActiveOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showActiveOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Vis kun aktive
              </button>
              <button
                onClick={() => setShowExpired(!showExpired)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showExpired
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Vis utgåtte
              </button>
              <button
                onClick={() => setShowWarehouseOnly(!showWarehouseOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showWarehouseOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Kun lagerførte
              </button>
              <button
                type="button"
                onClick={() => setFilterSystemsOnly(!filterSystemsOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  filterSystemsOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t("product.selector.filter_system_products")}
              </button>
              {!hideCategoryAndSystemNav && productTypeOptions.length > 0 && (
                <button
                  onClick={() => setShowProductTypeDialog(true)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors border ${
                    selectedCategory
                      ? "bg-blue-50 text-blue-700 border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
                  }`}
                >
                  {selectedCategory
                    ? productTypeOptions.find(opt => opt.value === selectedCategory)?.label || "Velg produkttype"
                    : "Velg produkttype"}
                </button>
              )}
            </div>
          )}

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "produkt" : "produkter"} valgt
            </div>
          )}
        </div>
      )}
      
      {!showHeader && (
        <div className="flex-shrink-0 p-2 sm:p-4 border-b border-gray-200">
          {/* Selected Products Toggle and Filter Toggle */}
          {(selectedIds.size > 0 || !searchOnly) && (
          <div className="flex items-center justify-between mb-2">
            {selectedIds.size > 0 && (
              <button
                onClick={() => setShowSelectedOnly(!showSelectedOnly)}
                className={`px-3 py-1.5 text-sm rounded-md transition-colors flex items-center gap-2 ${
                  showSelectedOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
                title="Vis kun valgte produkter"
              >
                <ListBulletIcon className="h-4 w-4" />
                <span>Vis valgte ({selectedIds.size})</span>
              </button>
            )}
            {!searchOnly && (
            <button
              onClick={() => setShowFilters(!showFilters)}
              className={`p-1.5 rounded-md transition-colors ml-auto ${
                showFilters
                  ? "bg-blue-100 text-blue-600"
                  : "text-gray-400 hover:text-gray-600"
              }`}
              title="Vis filtre"
            >
              <FunnelIcon className="h-5 w-5" />
            </button>
            )}
          </div>
          )}
          {/* Search Bar */}
          <div className="relative">
            <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
              <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
            </div>
            <input
              type="text"
              value={searchQuery}
              onChange={(e) => setSearchQuery(e.target.value)}
              placeholder={resolvedSearchPlaceholder}
              className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 sm:text-sm"
              autoFocus
            />
            {searchQuery && (
              <button
                onClick={() => setSearchQuery("")}
                className="absolute inset-y-0 right-0 pr-3 flex items-center"
              >
                <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
              </button>
            )}
          </div>

          {/* Mobile: Category and System Selector Buttons */}
          {!hideCategoryAndSystemNav && (
          <div className="md:hidden mt-2 space-y-2">
            <button
              onClick={() => setShowCategorySelectorMobile(true)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">
                {categoryBreadcrumb.length > 0
                  ? categoryBreadcrumb[categoryBreadcrumb.length - 1].title
                  : "Velg kategori"}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </button>
            <button
              onClick={() => setShowSystemProductsMobile(true)}
              className="w-full flex items-center justify-between px-3 py-2 border border-gray-300 rounded-md bg-white hover:bg-gray-50 transition-colors"
            >
              <span className="text-sm text-gray-700">
                {selectedSystemProductId
                  ? systemProducts.find(p => p.id === selectedSystemProductId)?.displayTitle || 
                    systemProducts.find(p => p.id === selectedSystemProductId)?.title ||
                    "Systemprodukt valgt"
                  : "Velg systemprodukt"}
              </span>
              <ChevronRightIcon className="h-4 w-4 text-gray-400" />
            </button>
          </div>
          )}

          {/* Breadcrumb Navigation - Desktop */}
          {!hideCategoryAndSystemNav && (categoryBreadcrumb.length > 0 || selectedSystemProductId) && (
            <div className="hidden md:flex mt-2 items-center gap-1 text-sm text-gray-600 flex-wrap">
              {categoryBreadcrumb.length > 0 && (
                <>
                  <button
                    onClick={() => handleBreadcrumbClick(-1)}
                    className="flex items-center gap-1 hover:text-blue-600"
                  >
                    <HomeIcon className="h-4 w-4" />
                    <span>Alle kategorier</span>
                  </button>
                  {categoryBreadcrumb.map((category, index) => (
                    <React.Fragment key={category.id}>
                      <ChevronRightIcon className="h-3 w-3 text-gray-400" />
                      <button
                        onClick={() => handleBreadcrumbClick(index)}
                        className="hover:text-blue-600 truncate max-w-[120px]"
                        title={category.title}
                      >
                        {category.title}
                      </button>
                    </React.Fragment>
                  ))}
                </>
              )}
              {selectedSystemProductId && (
                <>
                  {categoryBreadcrumb.length > 0 && (
                    <span className="mx-2 text-gray-400">|</span>
                  )}
                  <span className="text-gray-700">
                    System: {systemProducts.find(p => p.id === selectedSystemProductId)?.displayTitle || 
                             systemProducts.find(p => p.id === selectedSystemProductId)?.title || 
                             "Ukjent"}
                  </span>
                  <button
                    onClick={() => setSelectedSystemProductId(null)}
                    className="ml-2 text-gray-400 hover:text-red-600"
                    title="Fjern systemprodukt-filter"
                  >
                    <XMarkIcon className="h-4 w-4" />
                  </button>
                </>
              )}
            </div>
          )}

          {/* Filters - Minimal compact design */}
          {showFilters && !searchOnly && (
            <div className="mt-2 flex items-center gap-2 flex-wrap">
              <button
                onClick={() => setShowActiveOnly(!showActiveOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showActiveOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Vis kun aktive
              </button>
              <button
                onClick={() => setShowExpired(!showExpired)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showExpired
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Vis utgåtte
              </button>
              <button
                onClick={() => setShowWarehouseOnly(!showWarehouseOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  showWarehouseOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                Kun lagerførte
              </button>
              <button
                type="button"
                onClick={() => setFilterSystemsOnly(!filterSystemsOnly)}
                className={`px-2.5 py-1 text-xs rounded-md transition-colors ${
                  filterSystemsOnly
                    ? "bg-blue-600 text-white"
                    : "bg-gray-100 text-gray-700 hover:bg-gray-200"
                }`}
              >
                {t("product.selector.filter_system_products")}
              </button>
              {!hideCategoryAndSystemNav && productTypeOptions.length > 0 && (
                <button
                  onClick={() => setShowProductTypeDialog(true)}
                  className={`px-2.5 py-1 text-xs rounded-md transition-colors border ${
                    selectedCategory
                      ? "bg-blue-50 text-blue-700 border-blue-300"
                      : "bg-gray-100 text-gray-700 hover:bg-gray-200 border-gray-300"
                  }`}
                >
                  {selectedCategory
                    ? productTypeOptions.find(opt => opt.value === selectedCategory)?.label || "Velg produkttype"
                    : "Velg produkttype"}
                </button>
              )}
            </div>
          )}

          {/* Selected count */}
          {selectedIds.size > 0 && (
            <div className="mt-2 text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "produkt" : "produkter"} valgt
            </div>
          )}
        </div>
      )}

      {/* Mobile Category Selector Overlay */}
      {!hideCategoryAndSystemNav && showCategorySelectorMobile && (
        <div className="md:hidden fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex-shrink-0 p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Velg kategori</h3>
            <button
              onClick={() => {
                setShowCategorySelectorMobile(false);
                setCategoriesSearchQuery("");
              }}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              title="Lukk"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          {/* Search */}
          <div className="flex-shrink-0 px-4 pt-4 pb-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={categoriesSearchQuery}
                onChange={(e) => setCategoriesSearchQuery(e.target.value)}
                placeholder="Søk etter kategorier..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                autoFocus
              />
              {categoriesSearchQuery && (
                <button
                  onClick={() => setCategoriesSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingCategories ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : categories.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                Ingen kategorier funnet
              </div>
            ) : filteredCategories.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                {categoriesSearchQuery ? "Ingen kategorier funnet" : "Ingen kategorier"}
              </div>
            ) : (
              <div className="space-y-1">
                {renderCategoryTree(filteredCategories)}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Mobile System Products Selector Overlay */}
      {!hideCategoryAndSystemNav && showSystemProductsMobile && (
        <div className="md:hidden fixed inset-0 z-50 bg-white flex flex-col">
          <div className="flex-shrink-0 p-4 border-b border-gray-200 flex items-center justify-between">
            <h3 className="text-lg font-semibold text-gray-900">Velg systemprodukt</h3>
            <button
              onClick={() => setShowSystemProductsMobile(false)}
              className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
              title="Lukk"
            >
              <XMarkIcon className="h-5 w-5" />
            </button>
          </div>
          
          {/* Search */}
          <div className="flex-shrink-0 px-4 pt-4 pb-2">
            <div className="relative">
              <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                <MagnifyingGlassIcon className="h-5 w-5 text-gray-400" />
              </div>
              <input
                type="text"
                value={systemProductsSearchQuery}
                onChange={(e) => setSystemProductsSearchQuery(e.target.value)}
                placeholder="Søk etter systemprodukter..."
                className="block w-full pl-10 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                autoFocus
              />
              {systemProductsSearchQuery && (
                <button
                  onClick={() => setSystemProductsSearchQuery("")}
                  className="absolute inset-y-0 right-0 pr-3 flex items-center"
                >
                  <XMarkIcon className="h-5 w-5 text-gray-400 hover:text-gray-600" />
                </button>
              )}
            </div>
          </div>

          <div className="flex-1 overflow-y-auto p-4">
            {isLoadingSystemProducts ? (
              <div className="flex items-center justify-center py-8">
                <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
              </div>
            ) : systemProducts.length === 0 ? (
              <div className="text-sm text-gray-500 text-center py-4">
                {systemProductsSearchQuery ? "Ingen systemprodukter funnet" : "Søk etter systemprodukter"}
              </div>
            ) : (
              <div className="space-y-1">
                {systemProducts.map((product) => {
                  const isSelected = selectedSystemProductId === product.id;
                  return (
                    <div
                      key={product.id}
                      className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                        isSelected
                          ? "bg-blue-100 text-blue-700 font-medium"
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                    >
                      <div
                        className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                        onClick={() => handleSystemProductSelect(product)}
                      >
                        {product.main_picture && (
                          <div className="flex-shrink-0">
                            <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100">
                              <DocumentImage document={product.main_picture} />
                            </div>
                          </div>
                        )}
                        <div className="flex-1 min-w-0">
                          <p className="text-sm font-medium truncate">
                            {product.displayTitle || product.title}
                          </p>
                          {product.item_number && (
                            <p className="text-xs text-gray-500 truncate">
                              Art.nr: {product.item_number}
                            </p>
                          )}
                          <div
                            className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5"
                            onClick={(e) => e.stopPropagation()}
                          >
                            <Link
                              to={`/dashboard/products/${product.id}`}
                              onClick={(e) => e.stopPropagation()}
                              className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:underline"
                            >
                              <ArrowTopRightOnSquareIcon className="h-3 w-3 flex-shrink-0" />
                              {t("product.selector.open_product")}
                            </Link>
                            {getReplacementProductId(product) != null && (
                              <Link
                                to={`/dashboard/products/${getReplacementProductId(product)}`}
                                onClick={(e) => e.stopPropagation()}
                                className="inline-flex items-center gap-0.5 text-xs text-amber-800 hover:underline"
                              >
                                <ArrowsRightLeftIcon className="h-3 w-3 flex-shrink-0" />
                                {t("product.replaced_by.open_replacement")}
                              </Link>
                            )}
                          </div>
                        </div>
                        {isSelected && (
                          <CheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                        )}
                      </div>
                      {/* Info Button */}
                      <button
                        onClick={(e) => {
                          e.stopPropagation();
                          handleShowProductDetails(product);
                        }}
                        className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"
                        title="Vis detaljer"
                      >
                        <InformationCircleIcon className="h-5 w-5" />
                      </button>
                    </div>
                  );
                })}
              </div>
            )}
          </div>
        </div>
      )}

      {/* Main Content Area */}
      <div className="flex-1 flex min-h-0 overflow-hidden">
        {/* Sidebar - Desktop (larger size) */}
        {!hideCategoryAndSystemNav && (
        <div className="hidden md:flex flex-col w-80 lg:w-96 border-r border-gray-200 bg-gray-50 flex-shrink-0 min-h-0 overflow-hidden">
          {/* Tabs for Categories and System Products */}
          <div className="flex border-b border-gray-200 bg-white">
            <button
              onClick={() => setActiveView("categories")}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeView === "categories"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-gray-50"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              Kategorier
            </button>
            <button
              onClick={() => setActiveView("systems")}
              className={`flex-1 px-4 py-3 text-sm font-medium transition-colors ${
                activeView === "systems"
                  ? "text-blue-600 border-b-2 border-blue-600 bg-gray-50"
                  : "text-gray-600 hover:text-gray-900 hover:bg-gray-50"
              }`}
            >
              Systemprodukter
            </button>
          </div>

          {/* Categories View */}
          {activeView === "categories" && (
            <>
              {/* Search */}
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={categoriesSearchQuery}
                    onChange={(e) => setCategoriesSearchQuery(e.target.value)}
                    placeholder="Søk etter kategorier..."
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {categoriesSearchQuery && (
                    <button
                      onClick={() => setCategoriesSearchQuery("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {isLoadingCategories ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : categories.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    Ingen kategorier funnet
                  </div>
                ) : filteredCategories.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    {categoriesSearchQuery ? "Ingen kategorier funnet" : "Ingen kategorier"}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {renderCategoryTree(filteredCategories)}
                  </div>
                )}
              </div>
            </>
          )}

          {/* System Products View */}
          {activeView === "systems" && (
            <>
              {/* Search */}
              <div className="p-3 border-b border-gray-200 bg-white">
                <div className="relative">
                  <div className="absolute inset-y-0 left-0 pl-3 flex items-center pointer-events-none">
                    <MagnifyingGlassIcon className="h-4 w-4 text-gray-400" />
                  </div>
                  <input
                    type="text"
                    value={systemProductsSearchQuery}
                    onChange={(e) => setSystemProductsSearchQuery(e.target.value)}
                    placeholder="Søk etter systemprodukter..."
                    className="block w-full pl-9 pr-3 py-2 border border-gray-300 rounded-md leading-5 bg-white placeholder-gray-500 focus:outline-none focus:placeholder-gray-400 focus:ring-1 focus:ring-blue-500 focus:border-blue-500 text-sm"
                  />
                  {systemProductsSearchQuery && (
                    <button
                      onClick={() => setSystemProductsSearchQuery("")}
                      className="absolute inset-y-0 right-0 pr-3 flex items-center"
                    >
                      <XMarkIcon className="h-4 w-4 text-gray-400 hover:text-gray-600" />
                    </button>
                  )}
                </div>
              </div>

              <div className="flex-1 overflow-y-auto p-3">
                {isLoadingSystemProducts ? (
                  <div className="flex items-center justify-center py-8">
                    <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600"></div>
                  </div>
                ) : systemProducts.length === 0 ? (
                  <div className="text-sm text-gray-500 text-center py-4">
                    {systemProductsSearchQuery ? "Ingen systemprodukter funnet" : "Søk etter systemprodukter"}
                  </div>
                ) : (
                  <div className="space-y-1">
                    {systemProducts.map((product) => {
                      const isSelected = selectedSystemProductId === product.id;
                      return (
                        <div
                          key={product.id}
                          className={`flex items-center gap-3 px-3 py-2.5 rounded-md transition-colors ${
                            isSelected
                              ? "bg-blue-100 text-blue-700 font-medium"
                              : "hover:bg-gray-100 text-gray-700"
                          }`}
                        >
                          <div
                            className="flex items-center gap-3 flex-1 min-w-0 cursor-pointer"
                            onClick={() => handleSystemProductSelect(product)}
                          >
                            {product.main_picture && (
                              <div className="flex-shrink-0">
                                <div className="w-10 h-10 rounded-md overflow-hidden bg-gray-100">
                                  <DocumentImage document={product.main_picture} />
                                </div>
                              </div>
                            )}
                            <div className="flex-1 min-w-0">
                              <p className="text-sm font-medium truncate">
                                {product.displayTitle || product.title}
                              </p>
                              {product.item_number && (
                                <p className="text-xs text-gray-500 truncate">
                                  Art.nr: {product.item_number}
                                </p>
                              )}
                              <div
                                className="mt-1 flex flex-wrap items-center gap-x-2 gap-y-0.5"
                                onClick={(e) => e.stopPropagation()}
                              >
                                <Link
                                  to={`/dashboard/products/${product.id}`}
                                  onClick={(e) => e.stopPropagation()}
                                  className="inline-flex items-center gap-0.5 text-xs text-blue-600 hover:underline"
                                >
                                  <ArrowTopRightOnSquareIcon className="h-3 w-3 flex-shrink-0" />
                                  {t("product.selector.open_product")}
                                </Link>
                                {getReplacementProductId(product) != null && (
                                  <Link
                                    to={`/dashboard/products/${getReplacementProductId(product)}`}
                                    onClick={(e) => e.stopPropagation()}
                                    className="inline-flex items-center gap-0.5 text-xs text-amber-800 hover:underline"
                                  >
                                    <ArrowsRightLeftIcon className="h-3 w-3 flex-shrink-0" />
                                    {t("product.replaced_by.open_replacement")}
                                  </Link>
                                )}
                              </div>
                            </div>
                            {isSelected && (
                              <CheckIcon className="h-5 w-5 text-blue-600 flex-shrink-0" />
                            )}
                          </div>
                          {/* Info Button */}
                          <button
                            onClick={(e) => {
                              e.stopPropagation();
                              handleShowProductDetails(product);
                            }}
                            className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors flex-shrink-0"
                            title="Vis detaljer"
                          >
                            <InformationCircleIcon className="h-5 w-5" />
                          </button>
                        </div>
                      );
                    })}
                  </div>
                )}
              </div>
            </>
          )}
        </div>
        )}

        {/* Product List */}
        <div
          className={`flex-1 flex flex-col min-h-0 overflow-hidden ${
            !hideCategoryAndSystemNav && (showCategorySelectorMobile || showSystemProductsMobile)
              ? "md:flex hidden"
              : ""
          }`}
        >
          <div className="flex-1 overflow-y-auto min-h-0">
          {isLoading && products.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600 mx-auto mb-2"></div>
                <p className="text-sm text-gray-500">Laster produkter...</p>
              </div>
            </div>
          ) : error ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-sm text-red-600">{error}</p>
              </div>
            </div>
          ) : products.length === 0 ? (
            <div className="flex items-center justify-center h-64">
              <div className="text-center">
                <p className="text-sm text-gray-500">
                  {showSelectedOnly
                    ? "Ingen valgte produkter funnet"
                    : searchQuery || selectedCategoryIds.length > 0 || selectedSystemProductId
                    ? "Ingen produkter funnet"
                    : hideCategoryAndSystemNav
                    ? emptyListHintFlat
                    : "Søk etter produkter, velg en kategori eller systemprodukt"}
                </p>
              </div>
            </div>
          ) : (
            <div className="divide-y divide-gray-200 p-2 sm:p-3">
              {products.slice(0, 20).map((product) => {
                const productId = parseInt(product.id);
                const selected = !isNaN(productId) && isSelected(productId);
                const quantity = !isNaN(productId) ? (productQuantities.get(productId) || 1) : 1;
                return (
                  <div
                    key={product.id}
                    className={`w-full p-2 sm:p-3 border-b border-gray-100 last:border-b-0 ${
                      selected ? "bg-blue-50 border-l-4 border-l-blue-600" : ""
                    }`}
                  >
                    <div className="flex items-start space-x-2 sm:space-x-3">
                      {/* Checkbox */}
                      <div className="flex-shrink-0 mt-1">
                        <button
                          onClick={() => toggleProduct(product)}
                          className="focus:outline-none"
                        >
                          <div
                            className={`w-5 h-5 rounded border-2 flex items-center justify-center transition-colors ${
                              selected
                                ? "bg-blue-600 border-blue-600"
                                : "border-gray-300 hover:border-blue-400"
                            }`}
                          >
                            {selected && (
                              <CheckIcon className="h-4 w-4 text-white" />
                            )}
                          </div>
                        </button>
                      </div>

                      {/* Product Image */}
                      {product.main_picture && (
                        <div className="flex-shrink-0">
                          <div className="w-10 h-10 sm:w-12 sm:h-12 rounded-md overflow-hidden bg-gray-100">
                            <DocumentImage document={product.main_picture} />
                          </div>
                        </div>
                      )}

                      {/* Product Info */}
                      <div className="flex-1 min-w-0">
                        <div className="flex items-start justify-between gap-3">
                          <div className="flex-1 min-w-0">
                            <div className="flex items-center gap-2 flex-wrap">
                              <p className="text-sm font-medium text-gray-900 truncate">
                                {product.displayTitle || product.title}
                              </p>
                              {product.active === false && (
                                <span className="inline-flex items-center px-2 py-0.5 rounded text-xs font-medium bg-red-100 text-red-800">
                                  Ikke aktiv
                                </span>
                              )}
                              {product.warehouse_status_type_id && (
                                (() => {
                                  const status = findWarehouseStatusById(product.warehouse_status_type_id);
                                  if (status && status.value !== 0) {
                                    const statusColors = {
                                      1: "bg-green-100 text-green-800", // På lager
                                      2: "bg-yellow-100 text-yellow-800", // Vis varetelling
                                      3: "bg-blue-100 text-blue-800", // Bestillingsvare
                                      4: "bg-red-100 text-red-800", // Utgått
                                    };
                                    return (
                                      <span className={`inline-flex items-center px-2 py-0.5 rounded text-xs font-medium ${statusColors[status.value] || "bg-gray-100 text-gray-800"}`}>
                                        {status.label}
                                      </span>
                                    );
                                  }
                                  return null;
                                })()
                              )}
                              {getReplacementProductId(product) != null && (
                                <span
                                  className="inline-flex items-center gap-0.5 px-2 py-0.5 rounded text-xs font-medium bg-amber-100 text-amber-900"
                                  title={t("product.replaced_by.banner_description")}
                                >
                                  <ArrowsRightLeftIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                  {t("product.selector.replaced_label")}
                                </span>
                              )}
                            </div>
                            {product.item_number && (
                              <p className="text-xs text-gray-500 mt-0.5">
                                Art.nr: {product.item_number}
                              </p>
                            )}
                            {product.sales_price != null && (
                              <p className="text-xs text-gray-600 mt-1">
                                {typeof product.sales_price === 'number' 
                                  ? product.sales_price.toFixed(2) 
                                  : parseFloat(product.sales_price || 0).toFixed(2)} kr
                              </p>
                            )}
                            {(() => {
                              const replacementId = getReplacementProductId(product);
                              const pid = parseInt(product.id, 10);
                              if (Number.isNaN(pid)) return null;
                              return (
                                <div
                                  className="mt-1.5 flex flex-wrap items-center gap-x-3 gap-y-1"
                                  onClick={(e) => e.stopPropagation()}
                                >
                                  <Link
                                    to={`/dashboard/products/${pid}`}
                                    className="inline-flex items-center gap-1 text-xs font-medium text-blue-600 hover:text-blue-800 hover:underline"
                                  >
                                    <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                    {t("product.selector.open_product")}
                                  </Link>
                                  {replacementId != null && (
                                    <Link
                                      to={`/dashboard/products/${replacementId}`}
                                      className="inline-flex items-center gap-1 text-xs font-medium text-amber-800 hover:text-amber-950 hover:underline"
                                    >
                                      <ArrowTopRightOnSquareIcon className="h-3.5 w-3.5 flex-shrink-0" />
                                      {t("product.replaced_by.open_replacement")}
                                    </Link>
                                  )}
                                </div>
                              );
                            })()}
                          </div>

                          <div className="flex items-center gap-2">
                            {/* Quantity Input */}
                            {selected && showQuantityInput && (
                              <div className="flex-shrink-0">
                                <label className="block text-xs font-medium text-gray-700 mb-1">
                                  Antall
                                </label>
                                <input
                                  type="number"
                                  min="0.01"
                                  step="0.01"
                                  value={quantity}
                                  onChange={(e) =>
                                    updateQuantity(productId, e.target.value)
                                  }
                                  onClick={(e) => e.stopPropagation()}
                                  onFocus={(e) => e.stopPropagation()}
                                  className="w-16 sm:w-20 px-2 py-1 text-xs sm:text-sm border border-gray-300 rounded-md focus:outline-none focus:ring-1 focus:ring-blue-500 focus:border-blue-500"
                                  placeholder="1"
                                />
                              </div>
                            )}
                            
                            {/* Info Button */}
                            <div className="flex-shrink-0 mt-6">
                              <button
                                onClick={(e) => {
                                  e.stopPropagation();
                                  handleShowProductDetails(product);
                                }}
                                className="p-1.5 text-gray-400 hover:text-blue-600 transition-colors"
                                title="Vis detaljer"
                              >
                                <InformationCircleIcon className="h-5 w-5" />
                              </button>
                            </div>
                          </div>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>
          )}

          {/* Load More Button */}
          {hasMore && !isLoading && products.length < 20 && (
            <div className="p-4 text-center border-t border-gray-200">
              <button
                onClick={loadMore}
                className="text-sm text-blue-600 hover:text-blue-800 font-medium"
              >
                Last flere produkter
              </button>
            </div>
          )}

          {isLoading && products.length > 0 && (
            <div className="p-4 text-center">
              <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-blue-600 mx-auto"></div>
            </div>
          )}
          </div>
        </div>
      </div>

      {showConfirmSelection && (
        <div className="flex-shrink-0 border-t border-gray-200 bg-gray-50 px-3 py-3 sm:px-4">
          <div className="flex flex-col gap-3 sm:flex-row sm:items-center sm:justify-between">
            <div className="text-sm text-gray-600">
              {selectedIds.size} {selectedIds.size === 1 ? "produkt" : "produkter"} valgt
            </div>
            <div className="flex items-center justify-end gap-2">
              {onClose && (
                <button
                  type="button"
                  onClick={onClose}
                  className="rounded-md border border-gray-300 bg-white px-3 py-2 text-sm font-medium text-gray-700 transition-colors hover:bg-gray-50"
                >
                  Avbryt
                </button>
              )}
              <button
                type="button"
                onClick={() => onConfirmSelection()}
                disabled={selectedIds.size === 0}
                className="rounded-md bg-slate-900 px-4 py-2 text-sm font-medium text-white transition-colors hover:bg-slate-800 disabled:cursor-not-allowed disabled:bg-slate-300"
              >
                {confirmSelectionLabel}
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );

  const nestedDialogs = (
    <>
      {/* Product Details Dialog */}
      <Transition.Root show={showProductDetailsDialog} as={Fragment}>
        <Dialog as="div" className="relative" style={{ zIndex: 3100 }} onClose={() => setShowProductDetailsDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0" style={{ zIndex: 3100 }}>
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-md sm:my-8 sm:p-6">
                  <div className="flex items-center justify-between mb-4">
                    <Dialog.Title className="text-lg font-semibold text-gray-900">
                      Produktdetaljer
                    </Dialog.Title>
                    <button
                      onClick={() => setShowProductDetailsDialog(false)}
                      className="p-1.5 rounded-md text-gray-400 hover:text-gray-600 transition-colors"
                    >
                      <XMarkIcon className="h-5 w-5" />
                    </button>
                  </div>

                  {isLoadingProductDetails ? (
                    <div className="flex items-center justify-center py-8">
                      <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-blue-600"></div>
                    </div>
                  ) : selectedProductForDetails ? (
                    <div className="space-y-4">
                      {/* Product Image */}
                      {productDetails?.main_picture && (
                        <div className="flex justify-center">
                          <div className="w-32 h-32 sm:w-40 sm:h-40 rounded-lg overflow-hidden bg-gray-100">
                            <DocumentImage document={productDetails.main_picture} />
                          </div>
                        </div>
                      )}

                      {/* Product Title */}
                      <div>
                        <h3 className="text-lg font-semibold text-gray-900">
                          {productDetails?.displayTitle || productDetails?.title || selectedProductForDetails.displayTitle || selectedProductForDetails.title}
                        </h3>
                        {productDetails?.item_number && (
                          <p className="text-sm text-gray-500 mt-1">
                            Art.nr: {productDetails.item_number}
                          </p>
                        )}
                      </div>

                      {/* Short Description */}
                      {productDetails?.short_description && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-1">Kort beskrivelse</h4>
                          <div 
                            className="text-sm text-gray-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: productDetails.short_description }}
                          />
                        </div>
                      )}

                      {/* Description */}
                      {productDetails?.description && (
                        <div>
                          <h4 className="text-sm font-medium text-gray-700 mb-1">Beskrivelse</h4>
                          <div 
                            className="text-sm text-gray-600 prose prose-sm max-w-none"
                            dangerouslySetInnerHTML={{ __html: productDetails.description }}
                          />
                        </div>
                      )}

                      {/* Prices */}
                      <div className="grid grid-cols-2 gap-4 pt-2 border-t border-gray-200">
                        {productDetails?.sales_price != null && (
                          <div>
                            <p className="text-xs text-gray-500">Salgspris</p>
                            <p className="text-sm font-medium text-gray-900">
                              {typeof productDetails.sales_price === 'number' 
                                ? productDetails.sales_price.toFixed(2) 
                                : parseFloat(productDetails.sales_price || 0).toFixed(2)} kr
                            </p>
                          </div>
                        )}
                        {productDetails?.purchase_price != null && (
                          <div>
                            <p className="text-xs text-gray-500">Innkjøpspris</p>
                            <p className="text-sm font-medium text-gray-900">
                              {typeof productDetails.purchase_price === 'number' 
                                ? productDetails.purchase_price.toFixed(2) 
                                : parseFloat(productDetails.purchase_price || 0).toFixed(2)} kr
                            </p>
                          </div>
                        )}
                      </div>

                      {/* Stock Information */}
                      {productStockData && (
                        <div className="pt-2 border-t border-gray-200">
                          <h4 className="text-sm font-medium text-gray-700 mb-3">Beholdning</h4>
                          <div className="space-y-2">
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Sporet antall:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {productStockData.tracked_quantity != null 
                                  ? Number(productStockData.tracked_quantity).toFixed(2) 
                                  : "0.00"}
                              </span>
                            </div>
                            <div className="flex justify-between">
                              <span className="text-sm text-gray-600">Tilgjengelig:</span>
                              <span className="text-sm font-medium text-gray-900">
                                {productStockData.available_quantity != null 
                                  ? Number(productStockData.available_quantity).toFixed(2) 
                                  : "0.00"}
                              </span>
                            </div>
                            {productStockData.storages && productStockData.storages.length > 0 && (
                              <div className="mt-3">
                                <p className="text-xs font-medium text-gray-700 mb-2">Per lager:</p>
                                <div className="space-y-2">
                                  {productStockData.storages.map((storage) => (
                                    <div key={storage.id} className="bg-gray-50 rounded-md p-2">
                                      <p className="text-xs font-medium text-gray-900">{storage.title || storage.storage_number}</p>
                                      <div className="mt-1 space-y-1">
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-600">Sporet:</span>
                                          <span className="text-gray-900">
                                            {storage.tracked_quantity != null 
                                              ? Number(storage.tracked_quantity).toFixed(2) 
                                              : "0.00"}
                                          </span>
                                        </div>
                                        <div className="flex justify-between text-xs">
                                          <span className="text-gray-600">Tilgjengelig:</span>
                                          <span className="text-gray-900">
                                            {storage.available_quantity != null 
                                              ? Number(storage.available_quantity).toFixed(2) 
                                              : "0.00"}
                                          </span>
                                        </div>
                                      </div>
                                    </div>
                                  ))}
                                </div>
                              </div>
                            )}
                          </div>
                        </div>
                      )}

                      {/* Product page + replacement */}
                      {productDetails?.id && (
                        <div className="pt-2 border-t border-gray-200 space-y-2">
                          <Link
                            to={`/dashboard/products/${productDetails.id}`}
                            className="inline-flex items-center gap-1 text-sm font-medium text-blue-600 hover:text-blue-800 hover:underline"
                          >
                            <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-shrink-0" />
                            {t("product.selector.open_product")}
                          </Link>
                          {getReplacementProductId(productDetails) != null && (
                            <div className="rounded-md bg-amber-50 border border-amber-100 px-3 py-2 space-y-2">
                              <p className="text-xs font-medium text-amber-900">
                                {t("product.replaced_by.banner_title")}
                              </p>
                              <p className="text-xs text-amber-800">
                                {t("product.replaced_by.banner_description")}
                              </p>
                              <Link
                                to={`/dashboard/products/${getReplacementProductId(productDetails)}`}
                                className="inline-flex items-center gap-1 text-sm font-medium text-amber-900 hover:underline"
                              >
                                <ArrowTopRightOnSquareIcon className="h-4 w-4 flex-shrink-0" />
                                {t("product.replaced_by.open_replacement")}
                              </Link>
                            </div>
                          )}
                        </div>
                      )}

                      {/* Status Tags */}
                      <div className="flex flex-wrap gap-2 pt-2 border-t border-gray-200">
                        {productDetails?.active === false && (
                          <span className="inline-flex items-center px-2 py-1 rounded text-xs font-medium bg-red-100 text-red-800">
                            Ikke aktiv
                          </span>
                        )}
                        {productDetails?.warehouse_status_type_id && (
                          (() => {
                            const status = findWarehouseStatusById(productDetails.warehouse_status_type_id);
                            if (status && status.value !== 0) {
                              const statusColors = {
                                1: "bg-green-100 text-green-800",
                                2: "bg-yellow-100 text-yellow-800",
                                3: "bg-blue-100 text-blue-800",
                                4: "bg-red-100 text-red-800",
                              };
                              return (
                                <span className={`inline-flex items-center px-2 py-1 rounded text-xs font-medium ${statusColors[status.value] || "bg-gray-100 text-gray-800"}`}>
                                  {status.label}
                                </span>
                              );
                            }
                            return null;
                          })()
                        )}
                      </div>
                    </div>
                  ) : null}
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>

      {/* Product Type Selection Dialog */}
      <Transition.Root show={showProductTypeDialog} as={Fragment}>
        <Dialog as="div" className="relative" style={{ zIndex: 3100 }} onClose={() => setShowProductTypeDialog(false)}>
          <Transition.Child
            as={Fragment}
            enter="ease-out duration-300"
            enterFrom="opacity-0"
            enterTo="opacity-100"
            leave="ease-in duration-200"
            leaveFrom="opacity-100"
            leaveTo="opacity-0"
          >
            <div className="fixed inset-0 bg-gray-500 bg-opacity-75 transition-opacity" />
          </Transition.Child>

          <div className="fixed inset-0 " style={{ zIndex: 3100 }}>
            <div className="flex min-h-full items-center justify-center p-4 text-center">
              <Transition.Child
                as={Fragment}
                enter="ease-out duration-300"
                enterFrom="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
                enterTo="opacity-100 translate-y-0 sm:scale-100"
                leave="ease-in duration-200"
                leaveFrom="opacity-100 translate-y-0 sm:scale-100"
                leaveTo="opacity-0 translate-y-4 sm:translate-y-0 sm:scale-95"
              >
                <Dialog.Panel className="relative transform overflow-hidden rounded-lg bg-white px-4 pb-4 pt-5 text-left shadow-xl transition-all w-full max-w-xs sm:my-8 sm:p-6">
                  <Dialog.Title className="text-base font-semibold text-gray-900 mb-4">
                    Velg produkttype
                  </Dialog.Title>
                  
                  <div className="space-y-1">
                    <button
                      onClick={() => {
                        setSelectedCategory(null);
                        setShowProductTypeDialog(false);
                      }}
                      className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                        !selectedCategory
                          ? "bg-blue-100 text-blue-700 font-medium"
                          : "hover:bg-gray-100 text-gray-700"
                      }`}
                    >
                      Alle typer
                    </button>
                    {productTypeOptions.filter(opt => opt.value !== 0).map((opt) => (
                      <button
                        key={opt.value}
                        onClick={() => {
                          setSelectedCategory(opt.value);
                          setShowProductTypeDialog(false);
                        }}
                        className={`w-full text-left px-3 py-2 rounded-md text-sm transition-colors ${
                          selectedCategory === opt.value
                            ? "bg-blue-100 text-blue-700 font-medium"
                            : "hover:bg-gray-100 text-gray-700"
                        }`}
                      >
                        {opt.label}
                      </button>
                    ))}
                  </div>

                  <div className="mt-4 flex justify-end">
                    <button
                      onClick={() => setShowProductTypeDialog(false)}
                      className="px-3 py-1.5 text-sm text-gray-700 hover:text-gray-900"
                    >
                      Lukk
                    </button>
                  </div>
                </Dialog.Panel>
              </Transition.Child>
            </div>
          </div>
        </Dialog>
      </Transition.Root>
    </>
  );

  if (embeddedInParentDialog) {
    return (
      <>
        {mainPanel}
        {nestedDialogs}
      </>
    );
  }

  return (
    <>
      <PMDialog
        open={open}
        onClose={() => onClose && onClose()}
        title={pmdialogTitleKey}
        size={pmdialogSize}
        closeOnOutsideClick={true}
        zIndex={3000}
        dense={pmdialogDense}
      >
        <div
          className={
            pmdialogDense
              ? "flex flex-col min-h-0 w-full flex-1 overflow-hidden"
              : "flex flex-col min-h-0 w-full -mx-1 -mt-2"
          }
        >
          {mainPanel}
        </div>
      </PMDialog>
      {nestedDialogs}
    </>
  );
}
